import Service from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';
import StateService, { ProductRow } from './state';
import FilterService from './filter';
import HarmonicsData from '../models/harmonics/harmonics';
import type { HarmonicSequenceValue } from '../models/harmonics/harmonics';
import PPTSpur from '../models/ppt-spur';
import SpurGraph from '../models/graphs/spur-graph';
import WaveformGraph from 'simdds/models/graphs/waveform-graph';
import { sigFigsDown } from '../utils';
import URLService from './url';

const MINIMUM_CLOCKINPUT = 2;
const DEFAULT_PARTNUMBER = 'AD9914';

export default class CircuitService extends Service {
  @service declare state: StateService;
  @service declare url: URLService;
  @service declare filter: FilterService;
  @tracked inComponentExceededFtw = false;
  @tracked uncommittedFtwValue: number | null = null;

  get nonDDSQueryString(): string {
    return this.state.nonDDSQueryString;
  }

  get partData(): ProductRow | undefined {
    return this.state.partData;
  }

  get partNumber(): string {
    return this.state.partNumber;
  }
  set partNumber(value: string) {
    if (!this.partData) {
      value = DEFAULT_PARTNUMBER;
    }
    this.state.partNumber = value;
    this.inComponentExceededFtw = false;
    this.state.multiplier = 1;
    this.state.systemClock = this.partData?.masterFclk ?? 3.5e9;
    const clockInput = sigFigsDown(this.systemClock / this.multiplier, 8);
    this.state.inputClockFrequency = clockInput < MINIMUM_CLOCKINPUT ? MINIMUM_CLOCKINPUT : clockInput;
    this.state.ftw = this.isSystemClockInvalid || this.hasCriticalError ? Number.NaN : this._calculateFTW();
    this.state.finMoreRecentThanSClock = false;

    this.state.fc = this.nyquistFreq;
    this.updateParams(this.state.finMoreRecentThanSClock ? 'fin' : 'sclock');
    this.updateParams(this.state.ftwMoreRecentThanTof ? 'ftw' : 'tof');
    this.updateParams('part');
  }

  get multiplier(): number {
    return this.state.multiplier;
  }
  set multiplier(value: number) {
    const inputClockFrequency = sigFigsDown(this.systemClock / value, 8);
    this.state.multiplier = inputClockFrequency >= 2 ? value : 1;
    this.state.inputClockFrequency = inputClockFrequency >= 2 ? inputClockFrequency : MINIMUM_CLOCKINPUT;
    this.updateParams(this.state.finMoreRecentThanSClock ? 'fin' : 'sclock');
    this.updateParams(this.state.ftwMoreRecentThanTof ? 'ftw' : 'tof');
    this.updateParams('mult');
  }

  get clockInput(): number {
    return this.state.inputClockFrequency;
  }
  set clockInput(value: number) {
    if (value < MINIMUM_CLOCKINPUT || !value) {
      value = MINIMUM_CLOCKINPUT;
    }
    this.inComponentExceededFtw = false;
    this.state.inputClockFrequency = value;
    this.state.systemClock = value * this.multiplier;
    this.state.ftw = this.isSystemClockInvalid || this.hasCriticalError ? Number.NaN : this._calculateFTW();
    this.state.finMoreRecentThanSClock = true;
    this.updateParams(this.state.ftwMoreRecentThanTof ? 'ftw' : 'tof');
    this.updateParams('fin');
  }

  get systemClock(): number {
    return this.state.systemClock;
  }
  set systemClock(value: number) {
    if (value < MINIMUM_CLOCKINPUT || !value) {
      value = MINIMUM_CLOCKINPUT;
      this.state.multiplier = 1;
    }
    this.inComponentExceededFtw = false;
    this.state.systemClock = value;
    const clockInput = sigFigsDown(this.systemClock / this.multiplier, 8);
    this.state.inputClockFrequency = clockInput < MINIMUM_CLOCKINPUT ? MINIMUM_CLOCKINPUT : clockInput;
    this.state.ftw = this.isSystemClockInvalid || this.hasCriticalError ? Number.NaN : this._calculateFTW();
    this.state.finMoreRecentThanSClock = false;
    this.updateParams(this.state.ftwMoreRecentThanTof ? 'ftw' : 'tof');
    this.updateParams('sclock');
  }

  get maxClock(): number {
    return this.partData?.masterFclk || 1;
  }

  get minClock(): number {
    return sigFigsDown(this.maxClock / this.multiplier, 8);
  }

  get nyquistFreq(): number {
    const nyquistFreqByMaxClock = this.maxClock / 2;
    const nyquistFreqBySystemClock = this.systemClock / 2;
    return this.systemClock > this.maxClock
      ? sigFigsDown(nyquistFreqByMaxClock, 8)
      : sigFigsDown(nyquistFreqBySystemClock, 8);
  }

  get ftw(): number {
    return this.state.ftw;
  }
  set ftw(value: number) {
    let ftwValue = value;
    if (!isNaN(value)) {
      const targetFrequency = (value * this.systemClock) / Math.pow(2, this.partData?.tuningWordWidth ?? 32);
      if (this.validateValue('GT', this.nyquistFreq, targetFrequency)) {
        ftwValue = Number.NaN;
      }
      this.inComponentExceededFtw = false;
      this.state.targetFrequency = targetFrequency;
    }
    this.state.ftw = ftwValue;
    this.state.ftwMoreRecentThanTof = true;
    this.updateParams('ftw');
  }

  get targetFrequency(): number {
    return this.state.targetFrequency;
  }
  set targetFrequency(value: number) {
    if (value < 1 || !value) {
      value = 1;
    }
    this.inComponentExceededFtw = false;
    this.state.targetFrequency = value;
    this.state.ftw =
      this.isTargetFreqInvalid || this.hasCriticalError
        ? Number.NaN
        : Math.round((value * Math.pow(2, this.partData?.tuningWordWidth ?? 32)) / this.systemClock);
    this.state.ftwMoreRecentThanTof = false;
    this.updateParams('tof');
  }

  get ftwHex(): string {
    return isNaN(this.ftw) ? '' : this.ftw.toString(16).toUpperCase();
  }
  set ftwHex(hexValue: string) {
    this.ftw = parseInt(hexValue, 16);
  }

  get ftwBinary(): string {
    return isNaN(this.ftw) ? '' : this.ftw.toString(2).toUpperCase();
  }
  set ftwBinary(binaryValue: string) {
    this.ftw = parseInt(binaryValue, 2);
  }

  get actualFrequency(): number {
    const actualFrequency = (this.ftw * this.systemClock) / Math.pow(2, this.partData?.tuningWordWidth ?? 32);
    return sigFigsDown(actualFrequency, 12);
  }

  get harmonicsData(): HarmonicsData {
    return new HarmonicsData(this.systemClock, this.targetFrequency, this.state.harmonicDB, -50, this.filter);
  }

  get pptSpur(): PPTSpur {
    return new PPTSpur(this.partData as ProductRow, this.systemClock, this.ftwBinary, this.filter);
  }

  get hasCriticalError(): boolean {
    return this.isTargetFreqInvalid || this.isSystemClockInvalid || this.isExceededFtw;
  }

  get hasErrorOrWarning(): boolean {
    return (
      this.isTargetFreqInvalid ||
      this.isSystemClockInvalid ||
      this.isTargetFreqApprochingNyquistFreq ||
      this.isAttenuatedFundamental ||
      this.isZoomingRequired
    );
  }

  get spurGraphData(): SpurGraph {
    return new SpurGraph(this.systemClock, this.harmonicsData, this.pptSpur, this.filter);
  }

  get waveformGraphData(): WaveformGraph {
    return new WaveformGraph(this.targetFrequency, this.systemClock, this.harmonicsData);
  }

  get isTargetFreqApprochingNyquistFreq(): boolean {
    return this.targetFrequency >= 0.4 * this.systemClock;
  }

  get isSystemClockInvalid(): boolean {
    return this.validateValue('GT', this.maxClock, this.systemClock);
  }

  get isTargetFreqInvalid(): boolean {
    return this.validateValue('GT', this.nyquistFreq, this.targetFrequency);
  }

  get isZoomingRequired(): boolean {
    const harmonicsData = this.harmonicsData.rawHarmonicsTable;
    const allowance = this.systemClock / 50;
    const lowerBorderValue = this.targetFrequency - allowance;
    const upperBorderValue = this.targetFrequency + allowance;
    let setWarning = false;
    for (let i = 0; i < harmonicsData.length; i++) {
      const dataRow = harmonicsData[i] as HarmonicSequenceValue[]; // This is safe because we know this array isn't sparse
      for (let j = 0; j < dataRow.length; j++) {
        if (i === 0 && j === 0) {
          break;
        } else {
          const dataItem = dataRow[j] as HarmonicSequenceValue; // This is safe because we know this array isn't sparse
          if (
            parseFloat(dataItem.sequenceFrequency) > lowerBorderValue - 1 &&
            parseFloat(dataItem.sequenceFrequency) < upperBorderValue + 1
          ) {
            setWarning = true;
            break;
          }
        }
      }
    }
    return setWarning;
  }

  get isAttenuatedFundamental(): boolean {
    const fundamentalRow = this.harmonicsData.rawHarmonicsTable[0] as HarmonicSequenceValue[];
    const primaryFundamentalValue = fundamentalRow[0] as HarmonicSequenceValue;
    const unfilteredFundamentalValue = this.harmonicsData.unfilteredFundamentalHarmonic[0] as HarmonicSequenceValue;
    const filteredFundamental = parseFloat(primaryFundamentalValue.sequenceAmplitude);
    const unfilteredFundamental = parseFloat(unfilteredFundamentalValue.sequenceAmplitude);
    return filteredFundamental <= unfilteredFundamental - 1;
  }

  get isExceededFtw(): boolean {
    const tuningWordWidth = this.partData?.tuningWordWidth ?? 32;
    const maxFTW = Math.pow(2, tuningWordWidth) - 1;
    const currentFTW = this._calculateFTW();
    return currentFTW > maxFTW || this.inComponentExceededFtw;
  }

  _calculateFTW(): number {
    return Math.round((this.targetFrequency * Math.pow(2, this.partData?.tuningWordWidth ?? 32)) / this.systemClock);
  }

  updateParams(param?: string): void {
    this.url.setQueryParams(param);
  }

  validateValue(gtotlt: string, num1: string | number, num2: string | number): boolean {
    let retval = false;
    switch (gtotlt) {
      case 'GT':
        retval = num2 > num1;
        break;
      case 'GTEQ':
        retval = num2 >= num1;
        break;
      case 'LT':
        retval = num2 < num1;
        break;
      case 'LTEQ':
        retval = num2 <= num1;
        break;
    }
    return retval;
  }
}

declare module '@ember/service' {
  interface Registry {
    circuit: CircuitService;
  }
}
